import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { includes, path } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import { resetForm } from '@gef-ui/form-core/stream/transfer/control';

import ToggleButtonMui from 'components/atoms/GefMui/ToggleButton';
import acl from 'features/common/acl';
import dialog from 'features/common/dialog';
import taskFeedback from 'features/views/taskFeedback';
import { TASK_FEEDBACK_FORM_NAME } from 'features/views/taskFeedback/form';
import user from 'features/views/user';
import { hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck } from 'utils/permissionResolvers';

const { MPU_CHANGE_COMPLETION_TYPE, MPU_FULFILL_TASK, MPU_FULFILL_FOR_USER } = acl.constants;

export const FeedbackButton = (props) => {
	const dispatch = useDispatch();
	const next = useControlNext(TASK_FEEDBACK_FORM_NAME);

	const permissions = useSelector(user.selectors.getPermissions);
	const isMpuCompletionType = useSelector((state) =>
		hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck(state)([
			{
				id: MPU_CHANGE_COMPLETION_TYPE[0].id,
				attributes: [
					{
						key: 'completionTypes',
						value: [String(props.finishTypeId)],
					},
				],
			},
		]),
	);

	const isMpuFulfillTaskForUserTaskTypes = useSelector((state) =>
		hasPermissionByIdsAndIncludedValuesWithAllKeywordCheck(state)([
			{
				id: MPU_FULFILL_FOR_USER[0].id,
				attributes: [
					{
						key: 'taskTypes',
						value: [String(props.taskTypeId)],
					},
				],
			},
		]),
	);

	const handleClick = useCallback(
		(finishType) => () => {
			next(resetForm());
			dispatch(dialog.actions.clear());
			dispatch(taskFeedback.actions.openFeedback(finishType));
		},
		[dispatch, next],
	);

	const { item, statusId, solverIsLoggedUser, taskFinishTypeId, feedbackData } = props;

	const checkPermission = () => {
		const aclKey = MPU_FULFILL_TASK[0].id;
		const listOfPermittedFinishTypes = path([aclKey, 'values', 'completionTypes', 0, 'list'], permissions);

		// check if theres a nested "list" attribute, if so use it
		if (listOfPermittedFinishTypes) {
			return includes(String(item.finishTypeId), listOfPermittedFinishTypes);
		} else {
			// when list attribute doesnt exist - check if theres at least "all" value under completionTypes attribute
			const permitedFinishTypesWithoutList = path([aclKey, 'values', 'completionTypes'], permissions);
			if (permitedFinishTypesWithoutList) {
				// checking for 'all' value
				const containsTheAllValue = includes('all', permitedFinishTypesWithoutList);
				return containsTheAllValue;
			} else {
				return false;
			}
		}
	};

	const taskNotFinishedCriteria =
		statusId !== 3 &&
		checkPermission() &&
		(solverIsLoggedUser || (!solverIsLoggedUser && isMpuFulfillTaskForUserTaskTypes));

	const taskFinishedCriteria = statusId === 3 && isMpuCompletionType;

	const activeButtons = taskNotFinishedCriteria || taskFinishedCriteria;
	const selectedFeedback = feedbackData?.finishTypeId || taskFinishTypeId;

	return (
		<ToggleButtonMui
			className={item.finishTypeId === selectedFeedback ? 'selected' : null}
			label={item.shortDescription}
			onClick={handleClick(item)}
			selected={item.finishTypeId === selectedFeedback}
			disabled={item.finishTypeId !== taskFinishTypeId && !activeButtons}
		/>
	);
};

FeedbackButton.propTypes = {
	feedbackData: PropTypes.object,
	finishTypeId: PropTypes.number,
	item: PropTypes.object,
	solverIsLoggedUser: PropTypes.bool,
	statusId: PropTypes.number,
	taskFinishTypeId: PropTypes.number,
	taskTypeId: PropTypes.number,
};

export default FeedbackButton;
